// Typed wrappers for /v1/admin/payout-exports (migration 043).
//
// An export is a CLAIM on real money: creating one stamps the payouts it took
// so no other file can take them. That is why creating and downloading are
// separate calls — downloading is free and repeatable, creating is not.
//
// SUPERADMIN ONLY on the backend. BFF rule: everything through apiClient.
import { apiClient } from "./client";

export type ExportStatus = "open" | "settled" | "cancelled";

export interface PayoutExport {
  id: number;
  ref: string;
  bank_account_id: number;
  format_id: number;
  status: ExportStatus;
  row_count: number;
  total_amount_minor: number;
  created_at: string;
  settled_at?: string | null;
  cancelled_at?: string | null;
  note: string;
}

/** One payout line inside an export, for the resolve screen. */
export interface ExportLine {
  payout_id: number;
  status: string;
  amount_minor: number;
  beneficiary: string;
  account_no: string;
  ifsc: string;
  utr_no: string;
  error_message: string;
}

export interface EligibleSummary {
  bank_account_id: number;
  count: number;
  total_amount_minor: number;
}

/** What a new export WOULD claim. A read — it never takes anything. */
export async function getEligible(bankAccountID: number): Promise<EligibleSummary> {
  const r = await apiClient.get<EligibleSummary>(
    `/v1/admin/bank-accounts/${bankAccountID}/payout-exports/eligible`,
  );
  return r.data;
}

export async function listExports(params?: {
  bank_account_id?: number;
  status?: ExportStatus;
}): Promise<PayoutExport[]> {
  const r = await apiClient.get<{ exports: PayoutExport[] }>(
    "/v1/admin/payout-exports",
    { params },
  );
  return r.data.exports ?? [];
}

export async function getExport(
  id: number,
): Promise<{ export: PayoutExport; payouts: ExportLine[] }> {
  const r = await apiClient.get<{ export: PayoutExport; payouts: ExportLine[] }>(
    `/v1/admin/payout-exports/${id}`,
  );
  return { export: r.data.export, payouts: r.data.payouts ?? [] };
}

/**
 * createExport claims every waiting payout on a rail.
 *
 * `created: false` with reason "nothing_to_export" is a normal answer, not an
 * error — an operator who exports twice should be told the queue is empty.
 */
export async function createExport(input: {
  bank_account_id: number;
  format_id: number;
  note?: string;
}): Promise<{ created: boolean; export?: PayoutExport; message?: string }> {
  const r = await apiClient.post<{
    created: boolean;
    export?: PayoutExport;
    message?: string;
  }>("/v1/admin/payout-exports", input);
  return r.data;
}

export interface ExportResult {
  payout_id: number;
  outcome: "success" | "failure";
  utr_no?: string;
  error_message?: string;
}

export async function resolveExport(
  id: number,
  body: { results: ExportResult[]; mark_remaining_success?: boolean },
): Promise<{ succeeded: number; failed: number; export: PayoutExport }> {
  const r = await apiClient.post<{
    succeeded: number;
    failed: number;
    export: PayoutExport;
  }>(`/v1/admin/payout-exports/${id}/resolve`, body);
  return r.data;
}

export async function cancelExport(
  id: number,
  reason: string,
): Promise<PayoutExport> {
  const r = await apiClient.post<{ export: PayoutExport }>(
    `/v1/admin/payout-exports/${id}/cancel`,
    { reason },
  );
  return r.data.export;
}

/**
 * downloadExport fetches the file as a Blob.
 *
 * Kept as a real authenticated XHR rather than an <a href> to the endpoint:
 * the access token lives in memory only (CLAUDE.md rule 2), so a plain link
 * would arrive unauthenticated and 401.
 */
/**
 * ExportVariant selects which artefact a fixed-format rail hands back. A
 * layout-rendered rail has exactly one and ignores this.
 *
 *   xlsx    the bank's own workbook, filled in
 *   txt     what the portal ingests, skipping the spreadsheet
 *   payees  the SEPARATE payee-registration upload, which must be uploaded
 *           and approved before a transaction file has anyone to pay
 */
export type ExportVariant = "xlsx" | "txt" | "payees";

export async function downloadExport(
  id: number,
  variant?: ExportVariant,
): Promise<Blob> {
  const r = await apiClient.get(`/v1/admin/payout-exports/${id}/download`, {
    responseType: "blob",
    params: variant ? { format: variant } : undefined,
  });
  return r.data as Blob;
}
